// v4 (spec-v4-structure-and-stage.md 3.3, 5.3 "Upload sheet", 7.1-7.4; ticket V4-A2): one upload sheet for every kind
// of file at any level: pictures (products, places, props, drawings, a person for a character), logos, songs and theme
// music, sound effects, clips, intros and outros.
//
//   Add files to Milo & Friends
//   [ Choose files ]   or drop here           (drag-and-drop on desktop; several files at once)
//   Pictures 20 MB · Songs 100 MB · Sound effects 20 MB · Clips 500 MB
//   1.2 of 5 GB used ███░░░
//   ☐ I own these files or have permission…   (once per account: UPLOAD_TERMS_CONSENT, and again on a 428)
//   ☐ This is me, or I have this person's…    (a photo of a person: PERSON_PHOTO_CONSENT)
//   kitchen.jpg   [Place ▾]   ████████░ 80%   ✕ (cancel)
//   theme.mp3     done · tagged: warm, acoustic · 2:41   Edit
//
// It reuses api.uploadFile (start -> PUT with progress -> complete). Files are checked before a byte moves (type,
// size, room left) and the api's answers (too big, too long, quota, the 428) come back as one plain line on the file's
// row. They go one at a time; ✕ cancels the one sending (or drops one waiting). Auto tags arrive a moment after each
// upload (an "asset" event), and the row picks them up.
import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { api, ApiError, type StorageInfo } from "../api";
import { director } from "../director";
import { fmtBytes } from "../download";
import { PERSON_PHOTO_CONSENT, UPLOAD_TERMS_CONSENT, type ConsentCopy } from "../legal/content";
import {
  checkFile,
  DEFAULT_LIMITS,
  doneLine,
  guessWhat,
  limitsLine,
  mediaOf,
  quotaCheck,
  scopeKey,
  storageMeter,
  uploadErrorText,
  uploadScope,
  WHATS,
  whatsFor,
  type LibScope,
  type UploadKind,
  type UploadLimit,
  type UploadWhat,
} from "../library";
import { bump, toast, useRefresh } from "../store";
import type { Asset } from "../types";
import { Icon, Sheet } from "./bits";
import { openPicker } from "../picker";

type RowState = "ready" | "sending" | "done" | "failed";
type Row = { key: number; file: File; what: UploadWhat | null; state: RowState; sent: number; error: string | null; asset: Asset | null };

export type UploadSheetProps = {
  /** where the files go (the library's scope, a collection, a video...) */
  scope: LibScope;
  /** "Milo & Friends", "your library" */
  scopeName: string;
  /** what the files are when the caller knows ("Add an intro"); a file of another sort is still guessed */
  preset?: UploadWhat | null;
  /** files dropped on the page before the sheet opened */
  files?: File[];
  /** one file only (a logo, an intro) */
  single?: boolean;
  onClose: () => void;
  /** after a batch: the items made. `personConsent`: the person-photo box was ticked for them. */
  onDone?: (items: Asset[], ctx: { personConsent: boolean }) => void;
  /** "Edit" on a finished row */
  onEdit?: (a: Asset) => void;
};

let seq = 0;
const NOT_A_FILE_WE_TAKE = "That kind of file can't go in the library. Add pictures (PNG, JPEG, WebP), songs and sound effects (MP3, WAV, M4A) or clips (MP4, MOV).";
const ACCEPT: Record<"image" | "audio" | "video" | "any", string> = {
  image: "image/png,image/jpeg,image/webp",
  audio: "audio/*,.mp3,.wav,.m4a",
  video: "video/mp4,video/quicktime,.mp4,.mov",
  any: "image/png,image/jpeg,image/webp,audio/*,.mp3,.wav,.m4a,video/mp4,video/quicktime,.mov",
};

/** The per-kind limits the api sent (GET /api/me/storage), over the defaults. */
function limitsOf(info: StorageInfo | null): Record<UploadKind, UploadLimit> {
  const out = { ...DEFAULT_LIMITS };
  for (const k of Object.keys(out) as UploadKind[]) {
    const l = info?.limits?.[k];
    if (l && Array.isArray(l.mimes) && typeof l.maxBytes === "number") out[k] = { mimes: l.mimes, maxBytes: l.maxBytes, maxSeconds: l.maxSeconds };
  }
  return out;
}

export function UploadSheet({ scope, scopeName, preset = null, files, single = false, onClose, onDone, onEdit }: UploadSheetProps) {
  const [rows, setRows] = useState<Row[]>([]);
  const [info, setInfo] = useState<StorageInfo | null>(null);
  const [terms, setTerms] = useState<boolean | null>(null);
  const [ticked, setTicked] = useState(false);
  const [personTicked, setPersonTicked] = useState(false);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const ctrl = useRef<AbortController | null>(null);
  const stop = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  const limits = useMemo(() => limitsOf(info), [info]);

  // the terms checkbox up front (GET /api/me/storage), the limits and the meter
  useEffect(() => {
    let live = true;
    api
      .myStorage()
      .then((x) => {
        if (!live) return;
        setInfo(x);
        setTerms(x.termsAccepted);
      })
      .catch(() => live && setTerms(false));
    return () => {
      live = false;
    };
  }, []);

  const make = (file: File): Row => {
    const what = guessWhat(file, preset);
    return { key: ++seq, file, what, state: what ? "ready" : "failed", sent: 0, error: what ? null : NOT_A_FILE_WE_TAKE, asset: null };
  };
  const add = (list: FileList | File[] | null | undefined) => {
    const picked = Array.from(list ?? []);
    if (!picked.length) return;
    setRows((rs) => {
      const next = [...rs.filter((r) => !single || r.state === "done"), ...(single ? picked.slice(0, 1) : picked).map(make)];
      return next;
    });
  };
  useEffect(() => add(files), []); // eslint-disable-line react-hooks/exhaustive-deps

  // the checks before a byte moves, per row (a row's "what" can change, so these are derived, not stored)
  const problem = (r: Row) => (r.state === "failed" || r.state === "ready" ? (r.what ? checkFile(r.file, r.what, limits) : NOT_A_FILE_WE_TAKE) : null);
  const waiting = rows.filter((r) => r.state === "ready" && !problem(r));
  const needPerson = rows.some((r) => r.what === "person" && r.state !== "done");
  const termsNeeded = terms === false;
  const quota = info ? quotaCheck(waiting.reduce((n, r) => n + r.file.size, 0), info.usedBytes, info.quotaBytes) : null;
  const blocked = (termsNeeded && !ticked) || (needPerson && !personTicked) || !!quota;

  const patch = (key: number, p: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)));

  const run = async () => {
    if (running || !waiting.length || blocked) return;
    setRunning(true);
    stop.current = false;
    const made: Asset[] = [];
    for (const r of waiting) {
      if (stop.current) break;
      const w = WHATS[r.what!];
      const c = new AbortController();
      ctrl.current = c;
      patch(r.key, { state: "sending", sent: 0, error: null });
      try {
        const a = await api.uploadFile(r.file, { kind: w.kind, ...(w.role ? { role: w.role } : {}), scope: uploadScope(scope), ...(ticked ? { termsAccepted: true } : {}) }, (sent) => patch(r.key, { sent }), c.signal);
        made.push(a);
        setTerms(true);
        setInfo((x) => (x ? { ...x, usedBytes: x.usedBytes + r.file.size, termsAccepted: true } : x));
        patch(r.key, { state: "done", sent: 1, asset: a });
      } catch (e) {
        const err = e instanceof ApiError ? e : new ApiError((e as Error)?.message ?? "", 0);
        if (err.code === "CANCELLED") {
          setRows((rs) => rs.filter((x) => x.key !== r.key));
          continue;
        }
        if (err.code === "TERMS_NEEDED") {
          // the 428: ask for the box, keep the file waiting
          setTerms(false);
          setTicked(false);
          patch(r.key, { state: "ready", sent: 0, error: uploadErrorText(err, r.what!, limits) });
          break;
        }
        patch(r.key, { state: "failed", sent: 0, error: uploadErrorText({ status: err.status, code: err.code, message: err.message, details: err.details }, r.what!, limits) });
        if (err.code === "QUOTA_EXCEEDED" || err.code === "RATE_LIMITED") break;
      }
    }
    ctrl.current = null;
    setRunning(false);
    if (made.length) {
      bump("library");
      director.context(`The user added ${made.length === 1 ? `"${made[0]!.name}"` : `${made.length} files`} to ${scope.level === "account" ? "their library" : `the library of ${scopeName}`} (${scopeKey(scope)}).`);
      onDone?.(made, { personConsent: personTicked });
    }
  };

  const cancel = (r: Row) => {
    if (r.state === "sending") ctrl.current?.abort();
    else setRows((rs) => rs.filter((x) => x.key !== r.key));
  };
  const close = () => {
    stop.current = true;
    ctrl.current?.abort();
    onClose();
  };

  // auto tags land a moment after each upload ("asset" events bump the library): pick them up for finished rows
  const tick = useRefresh("library");
  const doneIds = rows.filter((r) => r.asset && !r.asset.autoTags?.length).map((r) => r.asset!.id);
  useEffect(() => {
    if (!doneIds.length) return;
    let live = true;
    api
      .library({ scope: scopeKey(scope), here: true })
      .then((items) => {
        if (!live) return;
        const by = new Map(items.map((a) => [a.id, a]));
        setRows((rs) => rs.map((r) => (r.asset && by.has(r.asset.id) ? { ...r, asset: by.get(r.asset.id)! } : r)));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (!running) add(e.dataTransfer?.files);
  };
  const meter = info ? storageMeter(info.usedBytes, info.quotaBytes) : null;
  const presetMedia = preset ? WHATS[preset].media : null;
  const one = preset ? WHATS[preset].label.toLowerCase() : "";
  const title = preset && single ? `Add ${/^[aeiou]/.test(one) ? "an" : "a"} ${one} to ${scopeName}` : `Add files to ${scopeName}`;

  return (
    <Sheet title={title} onClose={close} className="upsheet">
      <div className="sheet-body up-body">
        <div
          className={`up-drop${over ? " is-over" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={drop}
        >
          <button type="button" className="btn btn-primary" onClick={() => openPicker(input.current)} disabled={running} data-autofocus>
            <Icon name="plus" size={18} /> {single ? "Choose a file" : "Choose files"}
          </button>
          <span className="up-drop-hint">or drop {single ? "it" : "them"} here</span>
          <input ref={input} type="file" hidden multiple={!single} accept={ACCEPT[presetMedia ?? "any"]} onChange={(e) => (add(e.target.files), (e.target.value = ""))} />
        </div>
        <p className="up-limits muted">{limitsLine(limits)}</p>
        {meter && <StorageMeter info={info!} />}

        {termsNeeded && <Consent copy={UPLOAD_TERMS_CONSENT} checked={ticked} onChange={setTicked} className="up-terms" />}
        {needPerson && <Consent copy={PERSON_PHOTO_CONSENT} checked={personTicked} onChange={setPersonTicked} className="up-person" />}

        {!!rows.length && (
          <ul className="up-rows" aria-label="Files">
            {rows.map((r) => (
              <UploadRow key={r.key} r={r} problem={problem(r)} running={running} onWhat={(what) => patch(r.key, { what, error: null, state: "ready" })} onCancel={() => cancel(r)} onEdit={onEdit} />
            ))}
          </ul>
        )}

        {quota && (
          <p className="up-quota" role="alert">
            {quota}
          </p>
        )}

        <div className="up-foot">
          {rows.some((r) => r.state === "done") && !waiting.length && !running ? (
            <button type="button" className="btn btn-primary btn-block" onClick={close}>
              Done
            </button>
          ) : (
            <button data-action="upload.go" type="button" className="btn btn-primary btn-block" onClick={run} disabled={running || !waiting.length || blocked}>
              {running ? "Uploading…" : waiting.length ? `Upload ${waiting.length === 1 ? "1 file" : `${waiting.length} files`}` : "Upload"}
            </button>
          )}
          {blocked && !running && !!waiting.length && <p className="up-why muted">{quota ? "Not enough room." : termsNeeded && !ticked ? "Tick the box above first." : "Tick the box about the person first."}</p>}
        </div>
      </div>
    </Sheet>
  );
}

function UploadRow({ r, problem, running, onWhat, onCancel, onEdit }: { r: Row; problem: string | null; running: boolean; onWhat: (w: UploadWhat) => void; onCancel: () => void; onEdit?: (a: Asset) => void }) {
  const media = mediaOf(r.file);
  const options = media ? whatsFor(media) : [];
  const error = r.error ?? problem;
  const icon = media === "audio" ? "music" : media === "video" ? "film" : "image";
  const canPick = (r.state === "ready" || r.state === "failed") && !!options.length && !running;
  return (
    <li className={`up-row is-${r.state}${error ? " has-error" : ""}`}>
      <span className="up-ic" aria-hidden>
        {r.state === "done" ? <Icon name="check" size={18} /> : <Icon name={icon} size={18} />}
      </span>
      <div className="up-main">
        <span className="up-name">{r.file.name}</span>
        {r.state !== "done" && (
          <div className="up-sub">
            {canPick ? (
              <label className="up-what">
                <span className="sr-only">What {r.file.name} is</span>
                <select value={r.what ?? ""} onChange={(e) => onWhat(e.target.value as UploadWhat)}>
                  {options.map((w) => (
                    <option key={w} value={w}>
                      {WHATS[w].label}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              r.what && <span className="up-what-text muted">{WHATS[r.what].label}</span>
            )}
            {r.state === "sending" ? (
              <div className="up-prog" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(r.sent * 100)} aria-label={`Uploading ${r.file.name}`}>
                <span className="up-bar">
                  <span style={{ width: `${Math.round(r.sent * 100)}%` }} />
                </span>
                <span className="up-pct">{Math.round(r.sent * 100)}%</span>
              </div>
            ) : (
              <span className="up-size muted">{fmtBytes(r.file.size)}</span>
            )}
          </div>
        )}
        {r.state === "done" && r.asset ? (
          <p className="up-done">
            <span>done · {doneLine(r.asset)}</span>
            {onEdit && (
              <button type="button" className="linkish" onClick={() => onEdit(r.asset!)}>
                Edit
              </button>
            )}
          </p>
        ) : (
          error && r.state !== "sending" && <p className="up-err">{error}</p>
        )}
      </div>
      {r.state !== "done" && (
        <button type="button" className="icon-btn up-x" onClick={onCancel} aria-label={r.state === "sending" ? `Cancel ${r.file.name}` : `Remove ${r.file.name}`} title={r.state === "sending" ? "Cancel" : "Remove"} disabled={running && r.state !== "sending"}>
          <Icon name="close" size={16} />
        </button>
      )}
    </li>
  );
}

function Consent({ copy, checked, onChange, className }: { copy: ConsentCopy; checked: boolean; onChange: (v: boolean) => void; className: string }) {
  return (
    <label className={`up-check ${className}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {copy.label}{" "}
        <a href={copy.link.href} target="_blank" rel="noopener noreferrer">
          {copy.link.text}
        </a>
      </span>
    </label>
  );
}

/** "1.2 of 5 GB used" with a bar: uploads only (what the Director makes doesn't count). */
export function StorageMeter({ info, className = "" }: { info: StorageInfo; className?: string }) {
  const m = storageMeter(info.usedBytes, info.quotaBytes);
  return (
    <div className={`up-meter is-${m.tone} ${className}`}>
      <span className="up-meter-text">
        <span>Your uploads</span> <span className="muted">{m.text}</span>
      </span>
      {info.quotaBytes !== null && (
        <span className="up-meter-bar" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(m.share * 100)} aria-label="Upload storage used">
          <span style={{ width: `${Math.max(1, Math.round(m.share * 100))}%` }} />
        </span>
      )}
    </div>
  );
}

/** Whether a drag carries files (not text or a link dragged across the page). */
export const dragHasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");

/** For callers that toast instead of showing a row (one file, no sheet). */
export function toastUploadError(e: unknown, what: UploadWhat) {
  const err = e instanceof ApiError ? e : new ApiError((e as Error)?.message ?? "", 0);
  toast(uploadErrorText({ status: err.status, code: err.code, message: err.message, details: err.details }, what), "bad");
}
